import { useState } from 'react';
import { Target, ImageIcon } from 'lucide-react';
import { ASPECT_RATIOS } from './constants';
import { AspectRatioPreview } from './AspectRatioPreview';
import type { AspectRatioOption } from './types';

export interface SizeUsageOption {
  id: string;
  label: string;
  description: string;
  aspectRatio: string;
}

export const SIZE_USAGE_OPTIONS: SizeUsageOption[] = [
  { id: 'auto', label: '自动', description: '智能适配', aspectRatio: 'auto' },
  { id: 'id-photo-1', label: '证件照', description: '标准证件照', aspectRatio: '3:4' },
  { id: 'id-photo-2', label: '护照照片', description: '护照/签证照片', aspectRatio: '3:4' },
  { id: 'wechat-avatar', label: '微信头像', description: '正方形头像', aspectRatio: '1:1' },
  { id: 'xiaohongshu-cover', label: '小红书封面', description: '竖版封面图', aspectRatio: '3:4' },
  { id: 'douyin-vertical', label: '抖音竖图', description: '短视频封面', aspectRatio: '9:16' },
  { id: 'wallpaper-desktop', label: '电脑壁纸', description: '横版高清壁纸', aspectRatio: '16:9' },
  { id: 'ecommerce-main', label: '电商主图', description: '商品展示图', aspectRatio: '1:1' },
  { id: 'poster-horizontal', label: '横版海报', description: '宣传海报', aspectRatio: '16:9' },
  { id: 'wechat-official', label: '公众号封面', description: '文章封面图', aspectRatio: '3:2' },
];

interface SizeSelectorProps {
  aspectRatio: string;
  onAspectRatioChange: (value: string) => void;
  availableAspectRatios?: AspectRatioOption[];
}

export function SizeSelector({
  aspectRatio,
  onAspectRatioChange,
  availableAspectRatios = ASPECT_RATIOS,
}: SizeSelectorProps) {
  const [activeTab, setActiveTab] = useState<'usage' | 'ratio'>('usage');
  const [selectedUsageId, setSelectedUsageId] = useState<string | null>(null);
  const [hoveredRatio, setHoveredRatio] = useState<string | null>(null);
  const availableRatioValues = new Set(availableAspectRatios.map((ratio) => ratio.value));
  const availableUsageOptions = SIZE_USAGE_OPTIONS.filter((option) => availableRatioValues.has(option.aspectRatio));

  const handleUsageSelect = (usageId: string, ratio: string) => {
    setSelectedUsageId(usageId);
    onAspectRatioChange(ratio);
  };

  // 查找当前比例对应的用途
  const currentUsage = availableUsageOptions.find(u => u.aspectRatio === aspectRatio);

  return (
    <div>
      {/* 选项卡切换 */}
      <div className="card p-1 flex gap-1 mb-4">
        <button
          onClick={() => setActiveTab('usage')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'usage'
              ? 'bg-accent-blue text-white shadow-sm'
              : 'text-light-muted hover:text-light-text'
          }`}
        >
          <Target className="w-4 h-4" />
          图片用途
        </button>
        <button
          onClick={() => setActiveTab('ratio')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'ratio'
              ? 'bg-accent-blue text-white shadow-sm'
              : 'text-light-muted hover:text-light-text'
          }`}
        >
          <ImageIcon className="w-4 h-4" />
          输出比例
        </button>
      </div>

      {/* 图片用途面板 */}
      {activeTab === 'usage' && (
        <div className="animate-in fade-in duration-200">
          <div className="flex flex-wrap gap-2">
            {availableUsageOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleUsageSelect(option.id, option.aspectRatio)}
                data-active={selectedUsageId === option.id || (option.id === 'auto' && aspectRatio === 'auto' && !selectedUsageId)}
                className={`aspect-btn flex flex-col items-center py-2 px-3 h-auto min-h-[44px] ${
                  (selectedUsageId === option.id || (option.id === 'auto' && aspectRatio === 'auto' && !selectedUsageId)) ? 'ring-2 ring-accent-blue' : ''
                }`}
                title={option.description}
              >
                <span className="text-sm font-medium">{option.label}</span>
                <span className="text-[10px] opacity-60 mt-0.5 leading-tight">{option.description}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-light-muted pt-2">
            选择用途会自动切换比例，最终像素由下方画质决定
          </p>
        </div>
      )}

      {/* 输出比例面板 */}
      {activeTab === 'ratio' && (
        <div className="animate-in fade-in duration-200">
          <div className="flex flex-wrap gap-2">
            {availableAspectRatios.map((ratio) => (
              <div
                key={ratio.value}
                className="relative"
                onMouseEnter={() => setHoveredRatio(ratio.value)}
                onMouseLeave={() => setHoveredRatio((current) => (current === ratio.value ? null : current))}
              >
                {hoveredRatio === ratio.value && (
                  <div className="pointer-events-none absolute left-1/2 bottom-full z-20 mb-2 -translate-x-1/2 rounded-xl border border-light-border bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
                    <div className="flex min-w-[120px] items-center gap-3">
                      <div className="flex flex-col items-center gap-1 text-light-text">
                        <AspectRatioPreview ratio={ratio.value} size="md" />
                        <span className="text-[11px] font-medium">{ratio.label}</span>
                      </div>
                      <div className="text-xs leading-5 text-light-muted">
                        {ratio.description}
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => onAspectRatioChange(ratio.value)}
                  onFocus={() => setHoveredRatio(ratio.value)}
                  onBlur={() => setHoveredRatio((current) => (current === ratio.value ? null : current))}
                  className={`aspect-btn flex flex-col items-center py-2 px-3 h-auto min-h-[44px] ${
                    aspectRatio === ratio.value ? 'ring-2 ring-accent-blue' : ''
                  }`}
                  title={ratio.description}
                >
                  <span className="text-sm font-medium">{ratio.label}</span>
                  <span className="text-[10px] opacity-60 mt-0.5 leading-tight">{ratio.description}</span>
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-light-muted mt-3">
            画质会决定该比例对应的 1K / 2K / 4K 输出像素
          </p>
          {currentUsage && (
            <p className="text-xs text-accent-blue mt-3">
              当前比例适合: {currentUsage.label}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
